import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Table } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import CustomForm, { IFieldItem, IMyFormRef } from '@/components/CustomForm';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';
import invoiceService from '@/api/invoice';

import { DATE_TIME_FORMAT } from '@/config';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import { InvoiceOrderTypeEnum, orderTypeMap, OrderTypeEnum } from '../../constant';

interface IProps {
  ref: any;
  type: InvoiceOrderTypeEnum; // 订单类型
}

interface IBillParams {
  checkEndDateTime?: any;
  checkStartDateTime?: any;
  other?: string;
  pageNum: number;
  pageSize: number;
  resvNo?: string;
}

const Index: React.FC<IProps> = forwardRef((props, ref) => {
  const { type } = props;
  if (type === InvoiceOrderTypeEnum.other) return null;

  const [loading, setLoading] = useState<boolean>(false);
  // page
  const [page, setPage] = useState({ current: 1, pageSize: 50, total: 0 });
  const [dataSource, setDataSource] = useState<any[]>([]);

  // selected Orders
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);

  const searchFormRef = useRef<IMyFormRef>();

  const searchAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: intl.get('bill_invoice_modal_order_no'),
        key: 'resvNo',
        componentProps: {
          allowClear: true,
          maxLength: 30,
        },
      },
      {
        label: intl.get('bill_invoice_modal_guest_search_label'),
        key: 'other',
        placeholder: intl.get('bill_invoice_modal_please_input'),
        componentProps: {
          allowClear: true,
          maxLength: 30,
        },
      },
      {
        label: intl.get('bill_invoice_modal_checkout_time'),
        key: 'checkOutTime',
        type: 'dateRange',
        componentProps: {
          allowClear: true,
          showTime: 'HH:mm:ss',
          disabledDate: (currentDate) =>
            currentDate < moment().subtract(10, 'year') || currentDate > moment().add(10, 'year'),
        },
      },
    ];
  }, []);

  // 查询房间订单
  const queryRoomBillingOrders = async (params: IBillParams) => {
    setLoading(true);
    try {
      const { code, data } = await invoiceService.queryRoomBillingOrders(params);
      if (code === 0) {
        setDataSource(data?.list);
        setPage({ current: params?.pageNum, pageSize: params?.pageSize, total: data?.total || 0 });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // 数据初始化
  useEffect(() => {
    queryRoomBillingOrders({ pageNum: page.current, pageSize: page.pageSize });
  }, []);

  const getOrders = () => {
    return { orderList: selectedOrders };
  };

  useImperativeHandle(
    ref,
    () => ({
      getOrders,
    }),
    [selectedOrders],
  );

  const handleReset = () => {
    const params = {
      checkEndDateTime: null,
      checkStartDateTime: null,
      other: null,
      pageNum: 1,
      pageSize: page.pageSize,
      resvNo: '',
    };
    searchFormRef.current.resetFields();
    queryRoomBillingOrders(params);
  };

  const handleFinish = () => {
    const serchParams = searchFormRef.current.getFieldsValue();
    const { checkOutTime } = serchParams || [];
    const params = {
      checkEndDateTime: checkOutTime && checkOutTime[1] ? moment(checkOutTime[1]).valueOf() : null,
      checkStartDateTime:
        checkOutTime && checkOutTime[0] ? moment(checkOutTime[0]).valueOf() : null,
      other: serchParams?.other,
      pageNum: 1,
      pageSize: page.pageSize,
      resvNo: serchParams?.resvNo,
    };
    queryRoomBillingOrders(params);
  };

  const columns: any[] = [
    {
      title: intl.get('bill_invoice_modal_order_no'),
      dataIndex: 'orderNo',
      key: 'orderNo',
    },
    {
      title: intl.get('bill_invoice_modal_order_type'),
      dataIndex: 'orderType',
      key: 'orderType',
      width: 90,
      render: (val) => orderTypeMap[val], // 需与后端确认
    },
    {
      title: intl.get('bill_invoice_modal_room'),
      dataIndex: 'roomNo',
      key: 'roomNo',
    },
    {
      title: intl.get('bill_invoice_modal_guest'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.get('bill_invoice_modal_mobile_no'),
      dataIndex: 'mobile',
      key: 'mobile',
      render: (val) => formatAreaMobileStr(val),
    },
    {
      title: intl.get('bill_invoice_modal_cert_no'),
      dataIndex: 'certNo',
      key: 'certNo',
      width: 120,
      render: (val) => val || '-',
    },
    {
      title: intl.get('bill_invoice_modal_available_amt'),
      dataIndex: 'availableAmt',
      key: 'availableAmt',
      width: 120,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoiced_amt'),
      dataIndex: 'invoicedAmt',
      key: 'invoicedAmt',
      width: 120,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: intl.get('bill_invoice_modal_consume_amt'),
      dataIndex: 'consumeAmt',
      key: 'consumeAmt',
      width: 110,
      render: (val) => {
        return parseFen2Currency(val);
      },
    },
    {
      title: intl.get('bill_invoice_modal_operator_name'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 110,
      render: (val, record) => {
        return `${record?.updateBy || ''}${record?.operatorName || ''}`;
      },
    },
    {
      title: intl.get('bill_invoice_modal_checkout_time'),
      dataIndex: 'checkDateTime',
      key: 'checkDateTime',
      render: (val) => {
        if (!val) return '-';
        return getTimeByUtcOffset(val, DATE_TIME_FORMAT);
      },
    },
  ];

  const pageChange = (pageNo, pageSize) => {
    const serchParams = searchFormRef.current.getFieldsValue();
    const { checkOutTime } = serchParams;
    const params = {
      checkEndDateTime: checkOutTime && checkOutTime[1] ? moment(checkOutTime[1]).valueOf() : null,
      checkStartDateTime:
        checkOutTime && checkOutTime[0] ? moment(checkOutTime[0]).valueOf() : null,
      other: serchParams?.other,
      pageNum: pageNo,
      pageSize,
      resvNo: serchParams?.resvNo,
    };
    queryRoomBillingOrders(params);
  };

  const rowSelection: any = {
    onChange: (_selectedRowKeys, selectedRows) => {
      setSelectedOrders(selectedRows || []);
    },
  };

  return (
    <>
      <CustomForm
        ref={searchFormRef}
        layout="inline"
        submitType="filter"
        formAttr={searchAttr}
        onFinish={handleFinish}
        onReset={handleReset}
        showBtn
      />
      <Table
        rowKey="orderNo"
        columns={columns}
        dataSource={dataSource || []}
        loading={loading}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total: page.total,
          onChange: pageChange,
        }}
        scroll={{ x: 'max-content' }}
        style={{ marginTop: 22 }}
        rowSelection={{ ...rowSelection }}
        // sticky
      />
    </>
  );
});

export default Index;
